import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  Copy,
  ChevronDown,
  ChevronUp,
  Search,
  FileText,
  Target,
  Hash,
  BookOpen,
  RefreshCw
} from "lucide-react";
import { copyToClipboard } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

const FORMAT_ICONS = {
  "Listicle": "📝",
  "How-To Guide": "📋",
  "Ultimate Guide": "📚",
  "FAQ Post": "❓",
  "Problem-Solution": "💡"
};

const FORMAT_COLORS = {
  "Listicle": "#00d4aa",
  "How-To Guide": "#3b82f6",
  "Ultimate Guide": "#8b5cf6",
  "FAQ Post": "#f59e0b",
  "Problem-Solution": "#ef4444"
};

function SEOBlogsTab({ content, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  const [expandedPost, setExpandedPost] = useState(null);

  const copyFullPost = (post) => {
    const fullContent = `
# ${post.title}

**Meta Description:** ${post.metaDescription}

**Target Keyword:** ${post.targetKeyword}
**Secondary Keywords:** ${post.secondaryKeywords?.join(", ")}

---

${post.introduction}

${post.sections?.map(section => `## ${section.heading}\n\n${section.content}`).join("\n\n")}

---

${post.conclusion}

---
*Estimated Word Count: ${post.estimatedWordCount}*
    `.trim();
    
    copyToClipboard(fullContent);
  };

  const copyOutline = (post) => {
    const outline = `
# ${post.title}

## Outline:
${post.outline?.map((item, i) => `${i + 1}. ${item}`).join("\n")}

## Target Keywords:
- Primary: ${post.targetKeyword}
- Secondary: ${post.secondaryKeywords?.join(", ")}
    `.trim();
    
    copyToClipboard(outline);
  };

  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className="mb-4">No SEO blog content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="SEO Blogs" />}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-white">SEO Blog Variations</h2>
        <p className="text-white/60">
          {content.length} unique blog formats • Optimized for search engines
        </p>
      </div>

      {/* Blog Posts */}
      <div className="space-y-4">
        {content.map((post, index) => {
          const isExpanded = expandedPost === index;
          const color = FORMAT_COLORS[post.format] || "#00d4aa";
          const icon = FORMAT_ICONS[post.format] || "📝";

          return (
            <Card key={index} className="card overflow-hidden">
              {/* Header - Always Visible */}
              <CardHeader 
                className="cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedPost(isExpanded ? null : index)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div 
                      className="text-3xl p-2 rounded-lg"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      {icon}
                    </div>
                    <div className="space-y-1">
                      <div 
                        className="text-xs font-mono px-2 py-0.5 rounded inline-block"
                        style={{ backgroundColor: `${color}20`, color }}
                      >
                        {post.format}
                      </div>
                      <CardTitle className="text-white text-lg leading-tight">
                        {post.title}
                      </CardTitle>
                      <p className="text-white/60 text-sm line-clamp-2">
                        {post.metaDescription}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/40">
                      ~{post.estimatedWordCount} words
                    </span>
                    {onRegenerateItem && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRegenerateItem(index); }}
                        disabled={regeneratingItem === index}
                        className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                        title="Regenerate this blog"
                      >
                        <RefreshCw size={13} className={regeneratingItem === index ? 'animate-spin' : ''} />
                      </button>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-white/60" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white/60" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Expanded Content */}
              {isExpanded && (
                <CardContent className="border-t border-white/10 pt-6 space-y-6">
                  {/* Keywords */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#00d4aa] mb-2">
                        <Target className="w-4 h-4" />
                        <span className="text-sm font-semibold">Target Keyword</span>
                      </div>
                      <p className="text-white font-medium">{post.targetKeyword}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-purple-400 mb-2">
                        <Hash className="w-4 h-4" />
                        <span className="text-sm font-semibold">Secondary Keywords</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {post.secondaryKeywords?.map((kw, i) => (
                          <span 
                            key={i}
                            className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded"
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Outline */}
                  <div className="p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-blue-400">
                        <BookOpen className="w-4 h-4" />
                        <span className="text-sm font-semibold">Article Outline</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyOutline(post)}
                        className="text-[#00d4aa] hover:bg-[#00d4aa]/10"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Outline
                      </Button>
                    </div>
                    <ol className="space-y-1">
                      {post.outline?.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                          <span className="text-[#00d4aa] font-mono">{i + 1}.</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Introduction */}
                  <div className="p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-400 mb-3">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm font-semibold">Introduction</span>
                    </div>
                    <p className="text-white/80 text-sm whitespace-pre-line">
                      {post.introduction}
                    </p>
                  </div>

                  {/* Sections */}
                  {post.sections && post.sections.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-white font-semibold">Content Sections</h4>
                      {post.sections.map((section, i) => (
                        <div key={i} className="p-4 bg-white/5 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-white font-medium">{section.heading}</h5>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(`## ${section.heading}\n\n${section.content}`)}
                              className="text-[#00d4aa] hover:bg-[#00d4aa]/10"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-white/70 text-sm whitespace-pre-line">
                            {section.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Conclusion */}
                  <div className="p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-green-400 mb-3">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm font-semibold">Conclusion</span>
                    </div>
                    <p className="text-white/80 text-sm whitespace-pre-line">
                      {post.conclusion}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => copyFullPost(post)}
                      className="flex-1 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Full Article
                    </Button>
                    <Button
                      onClick={() => copyOutline(post)}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Outline Only
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Copy All Button */}
      <Button
        onClick={() => {
          const allContent = content.map(post => 
            `# ${post.format}: ${post.title}\n\nTarget Keyword: ${post.targetKeyword}\n\n${post.metaDescription}`
          ).join("\n\n---\n\n");
          copyToClipboard(allContent);
        }}
        className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold h-12"
      >
        <Copy className="w-4 h-4 mr-2" />
        Copy All Titles & Keywords
      </Button>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={() => onRegenerate('seoblogs')}
          isRegenerating={isRegenerating}
          contentType="SEO Blogs"
        />
      )}
    </div>
  );
}

export default SEOBlogsTab;
